/* eslint-disable no-useless-escape */
import dayjs from 'dayjs';
import * as yup from 'yup';
import { AnySchema } from 'yup';
import Lazy from 'yup/lib/Lazy';
import Reference from 'yup/lib/Reference';

import { IErrorObject } from '../../../utils/validator';

export enum WRITER_REGISTER_CAMPAIGN_FIELD_KEYS {
  TITLE = 'title',
  CONDITIONS = 'conditions',
  REGISTER_START_DATE = 'registerStartDate',
  REGISTER_END_DATE = 'registerEndDate',
  PERIOD_START_DATE = 'periodStartDate',
  PERIOD_END_DATE = 'periodEndDate',
  MINIMUM_DISCOUNT_PERCENTAGE = 'minimumDiscountPercentage',
  PROMOTE_TEMPLATE_IMAGE_URL = 'promoteTemplateImageUrl',
  BANNER_IMAGE_URL = 'bannerImageUrl',
  HASHTAG = 'hashtag',
  USER_TYPE = 'userType',
  CATEGORY = 'category',
  CATEGORY_IDS = 'categoryIds',
  EXCEPT_CATEGORY_IDS = 'exceptCategoryIds',
  SPECIFIC_WRITER_USER_IDS = 'specificWriterUserIds',
}

export const USER_TYPE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'UGC', value: 'ugc' },
  { label: 'Publisher', value: 'pgc' },
];

export const CATEGORY_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Fiction', value: 'fiction' },
  { label: 'Non-Fiction', value: 'non-fiction' },
  { label: 'Romance', value: 'romance' },
  { label: 'Fantasy', value: 'fantasy' },
  { label: 'Sci-Fi', value: 'sci-fi' },
  { label: 'Mystery', value: 'mystery' },
  { label: 'Horror', value: 'horror' },
  { label: 'Comedy', value: 'comedy' },
  { label: 'Drama', value: 'drama' },
];

export interface IErrorsWriterRegisterCampaign {
  title?: IErrorObject;
  conditions?: IErrorObject;
  registerStartDate?: IErrorObject;
  registerEndDate?: IErrorObject;
  periodStartDate?: IErrorObject;
  periodEndDate?: IErrorObject;
  minimumDiscountPercentage?: IErrorObject;
  promoteTemplateImageUrl?: IErrorObject;
  bannerImageUrl?: IErrorObject;
  hashtag?: IErrorObject;
  userType?: IErrorObject;
  category?: IErrorObject;
  categoryIds?: IErrorObject;
  exceptCategoryIds?: IErrorObject;
  specificWriterUserIds?: IErrorObject;
}

export type IWriterRegisterCampaignFields = keyof IErrorsWriterRegisterCampaign;

const WriterRegisterCampaignFormObj: { [key in IWriterRegisterCampaignFields]: AnySchema<unknown, unknown, unknown> | Reference<unknown> | Lazy<never, unknown> } = {
  title: yup.string().nullable().required('Campaign title is required').trim(),
  conditions: yup
    .array()
    .of(yup.string().required())
    .nullable()
    .test('has-condition', 'At least one condition is required', (value) => {
      return !!value?.some((item) => !!item?.trim());
    }),
  registerStartDate: yup
    .string()
    .nullable()
    .required('Register start date is required')
    .test('register-start-date-more-than-one-day-from-now', 'Register start date must be more than one day from now', (value) => {
      if (!value) return false;
      return dayjs(value).isAfter(dayjs(), 'day');
    }),
  registerEndDate: yup
    .string()
    .nullable()
    .required('Register end date is required')
    .test('registration-ended-at-must-be-before-started-at', 'Register end date must be before campaign start date', function (value) {
      if (!value) return false;
      const periodStartDate = this.parent.periodStartDate;
      if (!periodStartDate) return true;
      return dayjs(value).isBefore(dayjs(periodStartDate));
    }),
  periodStartDate: yup.string().nullable().required('Period start date is required'),
  periodEndDate: yup.string().nullable().required('Period end date is required'),
  minimumDiscountPercentage: yup
    .number()
    .nullable()
    .required('Minimum discount percentage is required')
    .min(1, 'Minimum discount must be at least 1%')
    .max(100, 'Maximum discount cannot exceed 100%'),
  promoteTemplateImageUrl: yup.string().nullable(),
  bannerImageUrl: yup.string().nullable(),
  hashtag: yup.array().of(yup.string().required()).nullable(),
  userType: yup.string().nullable().required('User type is required'),
  category: yup.string().nullable().required('Category is required'),
  categoryIds: yup
    .array()
    .of(yup.number().required())
    .nullable()
    .when('category', {
      is: 'specific',
      then: (schema) => schema.min(1, 'Please select at least one category'),
      otherwise: (schema) => schema,
    }),
  exceptCategoryIds: yup.array().of(yup.number().required()).nullable(),
  specificWriterUserIds: yup
    .array()
    .of(yup.string().required())
    .nullable()
    .when('userType', {
      is: 'specific',
      then: (schema) => schema.min(1, 'Please select at least one specific writer'),
      otherwise: (schema) => schema,
    }),
};

export default WriterRegisterCampaignFormObj;
